/**
 * BLK-36 — whether this page load should try to restore a session at all.
 *
 * The access token is deliberately in-memory only, so `getAccessToken()` is
 * always null on a fresh load and the app had no way to tell "returning vendor"
 * from "someone who has never signed in". It therefore POSTed
 * `/auth/refresh-token` on every single load, and for a signed-out visitor that
 * is a guaranteed 401 — which the login page waited for before painting,
 * because the whole thing sat in a blocking APP_INITIALIZER.
 *
 * A persisted flag closes that. It is a HINT, not a credential: it grants
 * nothing, the httpOnly refresh cookie remains the only authority, and forging
 * it buys an attacker exactly one 401. trans-web already works this way
 * (`AUTH_STATUS_KEY` in its auth store); this is the same idea.
 */
const SESSION_FLAG_KEY = 'vendorHasSession';

/**
 * Pure decision, kept separate from the storage read so it can be tested.
 * Anything other than the exact flag value means "no session to restore".
 */
export function shouldAttemptSessionRestore(storedFlag: string | null): boolean {
  return storedFlag === 'true';
}

export function readSessionFlag(): string | null {
  try {
    return localStorage.getItem(SESSION_FLAG_KEY);
  } catch {
    // Private mode / storage disabled: fall back to attempting the restore.
    // A wasted 401 is better than a vendor who can never stay signed in.
    return 'true';
  }
}

export function markSessionStarted(): void {
  try {
    localStorage.setItem(SESSION_FLAG_KEY, 'true');
  } catch {
    /* ignore */
  }
}

export function clearSessionFlag(): void {
  try {
    localStorage.removeItem(SESSION_FLAG_KEY);
  } catch {
    /* ignore */
  }
}
